import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useId } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import { taskTarget } from "@/features/collaboration/lib/links";
import { useCreateStandaloneTask, useCreateTask } from "@/features/tasks/api";
import { NewTaskPeopleRow } from "@/features/tasks/components/NewTaskPeopleRow";
import { NewTaskProjectField } from "@/features/tasks/components/NewTaskProjectField";
import { PriorityIcon } from "@/features/tasks/components/PriorityIcon";
import { useTaskParam } from "@/features/tasks/hooks/useTaskParam";
import { BOARD_COLUMNS, TASK_TITLE_MAX_LENGTH, type Task } from "@/features/tasks/lib/board";
import {
  NEW_TASK_DEFAULTS,
  NEW_TASK_SERVER_FIELDS,
  newTaskSchema,
  type NewTaskInput,
  type NewTaskOutput,
} from "@/features/tasks/lib/newTaskForm";
import { PRIORITIES } from "@/features/tasks/lib/priority";
import { applyServerErrors } from "@/shared/forms/serverErrors";
import { useT } from "@/shared/i18n/useT";
import type { MessageId } from "@/shared/i18n/messages";
import { Button } from "@/shared/ui/Button";
import { ChoiceCards } from "@/shared/ui/ChoiceCards";
import { Dialog } from "@/shared/ui/Dialog";
import { Field, describedBy } from "@/shared/ui/Field";
import { FormError } from "@/shared/ui/FormError";
import { Input } from "@/shared/ui/Input";
import { StatusIcon } from "@/shared/ui/StatusIcon";

interface NewTaskDialogProps {
  /**
   * The project the task goes in, as on a project's page. Leave it out to let the person pick
   * a project, or none: a task outside any project (T-12).
   */
  projectKey?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Create a task with its title, status, people, due date and priority. */
export function NewTaskDialog({ projectKey, open, onOpenChange }: NewTaskDialogProps) {
  const t = useT();
  const formId = useId();
  const anywhere = projectKey === undefined;
  const createInProject = useCreateTask(projectKey ?? "");
  const createAnywhere = useCreateStandaloneTask();
  const { openTask } = useTaskParam();
  const navigate = useNavigate();
  const form = useForm<NewTaskInput, unknown, NewTaskOutput>({
    resolver: zodResolver(newTaskSchema),
    defaultValues: NEW_TASK_DEFAULTS,
  });
  const { register, control, reset, setError, setValue } = form;
  const { errors, isSubmitting } = form.formState;
  const chosenProject = useWatch({ control, name: "project_key" });

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  // On a project's page the panel opens over the board; elsewhere the task's own page opens.
  const openCreated = (task: Task) => {
    if (!anywhere) {
      openTask(task.reference);
      return;
    }
    const target = taskTarget(task.project?.key ?? null, task.reference);
    void navigate(target.to, { state: target.state });
  };

  const onSubmit = form.handleSubmit(async ({ project_key, ...values }) => {
    try {
      const task = anywhere
        ? await createAnywhere.mutateAsync({ ...values, project_key })
        : await createInProject.mutateAsync(values);
      toast.success(t("newTask.created", { reference: task.reference }), {
        action: {
          label: t("task.open"),
          onClick: () => {
            openCreated(task);
          },
        },
      });
      onOpenChange(false);
    } catch (error) {
      applyServerErrors(error, setError, NEW_TASK_SERVER_FIELDS);
    }
  });

  const message = (id: string | undefined) => (id ? t(id as MessageId) : undefined);
  const titleError = message(errors.title?.message);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("newTask.title")}
      description={t(anywhere ? "newTask.descriptionAnywhere" : "newTask.description")}
      initialFocus={() => {
        form.setFocus("title");
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" form={formId} disabled={isSubmitting}>
            {isSubmitting ? t("newTask.creating") : t("newTask.create")}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className="flex flex-col gap-5"
        onSubmit={(event) => void onSubmit(event)}
        noValidate
      >
        <Field id="task-title" label={t("newTask.titleLabel")} error={titleError}>
          <Input
            dir="auto"
            maxLength={TASK_TITLE_MAX_LENGTH}
            placeholder={t("board.addTaskPlaceholder")}
            {...describedBy("task-title", titleError)}
            {...register("title")}
          />
        </Field>

        {anywhere ? (
          <NewTaskProjectField
            control={control}
            error={message(errors.project_key?.message)}
            onProjectChange={() => {
              // The people differ from project to project (all staff outside one).
              setValue("assignee_ids", []);
            }}
          />
        ) : null}

        <ChoiceCards
          legend={t("newTask.statusLabel")}
          options={BOARD_COLUMNS.map((value) => ({
            value,
            label: t(`board.status.${value}`),
            icon: <StatusIcon status={value} />,
          }))}
          columns={3}
          size="sm"
          {...register("status")}
        />

        <NewTaskPeopleRow
          control={control}
          projectKey={anywhere ? (chosenProject ?? null) : projectKey}
          enabled={open}
          assigneeError={message(errors.assignee_ids?.message)}
          dueError={message(errors.due_on?.message)}
        />

        <ChoiceCards
          legend={t("task.priority")}
          options={PRIORITIES.map((value) => ({
            value,
            label: t(`priority.${value}`),
            icon: <PriorityIcon priority={value} />,
          }))}
          columns={3}
          size="sm"
          {...register("priority")}
        />

        <FormError errors={errors} />
      </form>
    </Dialog>
  );
}
